import { memo } from "react"
import { TbCircleLetterDFilled } from "react-icons/tb";
import { IoMdHand } from "react-icons/io";
import { GiFootprint } from "react-icons/gi";

// ── Truco point values at each bet level ─────────────────────────────────────
// Level 0 = no bet yet  → 1 pt at stake (base)
// Level 1 = Truco       → 2 pts if accepted
// Level 2 = Re-Truco    → 3 pts if accepted
// Level 3 = Cuatro      → 4 pts if accepted
const TRUCO_LEVEL_LABEL = ["—", "Truco", "Re-Truco", "Cuatro"]
const TRUCO_LEVEL_PTS = [1, 2, 3, 4]

// ── Dot colours for each trick outcome ───────────────────────────────────────
const TRICK_DOT_STYLE = {
    win: { background: "#22c55e" },   // green
    lose: { background: "#ef4444" },   // red
    draw: { background: "#ffff00" },   // grey
    null: { background: "rgba(255, 255, 255, 0.07)", border: "2px solid rgba(255, 255, 255, 0.22)" },  // empty / not yet played
}

/**
 * PointsAndScoring
 *
 * Props:
 *   seats      — array of seat objects from reducer (each has playerId, isMe, trickWins[], points)
 *   turn       — { currentSeat, dealerSeat } from reducer
 *   bet        — { phase, trucoLevel, playingPoints } from reducer
 *   myPlayerId — local player id (from sessionStorage via reducer)
 *   points     — { myPoints, opponentPoints, playingPoints }
 *   maxPoints  — match winning threshold (15)
 */
function PointsAndScoring({ seats = [], turn = {}, bet = {}, myPlayerId, points = {}, maxPoints = 15, handIndex, dealerIndex }) {
    // ── Current trick points at stake ─────────────────────────────────────────
    // If a Truco bet is pending/active, show the points it would lock in.
    // Otherwise show current playingPoints (already accepted or base value).
    const isTrucoBetActive = bet.phase === "TRUCO"
    const pendingPts = isTrucoBetActive
        ? TRUCO_LEVEL_PTS[Math.min(bet.trucoLevel, 3)]
        : (points.playingPoints ?? 1)
    const betLabel = isTrucoBetActive
        ? TRUCO_LEVEL_LABEL[Math.min(bet.trucoLevel, 3)]
        : null

    // ── Build ordered row list: local player first, then opponents ────────────
    // seats already comes in rotated (local player is at display index 0 after rotation)
    // but we sort explicitly so this component is self-contained.
    const ordered = [...seats].sort((a, b) => {
        if (a.isMe && !b.isMe) return -1
        if (!a.isMe && b.isMe) return 1
        return 0
    })
    return (
        <div className="pointsAndScoringNotation">
            {/* <div className="pointsAndScoringBackGlow"></div> */}
            <div className="pointsAndScoringDivider" />
            <table className="playerPointsNotation">
                <tbody>
                    {ordered.map((seat, index) => {
                        const isTheirTurn = seat.serverSeatNumber === turn.currentSeat
                        const trickWins = seat.trickWins ?? [null, null, null]
                        const matchPoints = seat.isMe
                            ? (points.myPoints ?? 0)
                            : (points.opponentPoints ?? 0)
                        const name = seat.playerId
                            // ? (seat.isMe ? `${seat.playerId} (You)` : seat.playerId)
                            ? (seat.isMe ? `${seat.playerId}` : seat.playerId)
                            : (seat.isMe ? "You" : "Opponent")

                        return (
                            <tr key={seat.id ?? seat.playerId ?? seat.serverSeatNumber}>
                                <td className="handAndFootRepresentation">{seat.dealer ? <GiFootprint />
                                    : <IoMdHand />}</td>
                                {/* ── Player name ────────────────────────── */}
                                <td className="playerNameScoreDetails">
                                    {/* ── Turn indicator ─────────────────────── */}
                                    <p style={{ width: 'maxContent' }}>
                                        <span>{name}</span> {isTheirTurn && (
                                            < span
                                                title="Current turn"
                                                style={{
                                                    display: "inline-block",
                                                    width: 15,
                                                    height: 15,
                                                    borderRadius: "50%",
                                                    background: "#ef4444",
                                                    boxShadow: "0 0 4px #ef4444",
                                                    flexShrink: 0,
                                                    animation: "trucoTurnGlowBack 1.5s infinite",
                                                    "--start-box-shadow": "0 0 4px 5px rgb(232 102 102)",
                                                    "--end-box-shadow": "0 0 20px 0px rgb(232 102 102)"
                                                }}
                                            />
                                        )}
                                    </p>
                                </td>

                                {/* ── Trick-win dots ─────────────────────── */}
                                <td className="pointsNotationText">
                                    <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                                        {trickWins.map((outcome, i) => (
                                            <span
                                                key={i}
                                                title={outcome ?? "not played"}
                                                style={{
                                                    display: "inline-block",
                                                    width: 25,
                                                    height: 25,
                                                    borderRadius: "50%",
                                                    ...(TRICK_DOT_STYLE[outcome] ?? TRICK_DOT_STYLE.null)
                                                }}
                                            />
                                        ))}
                                    </div>
                                </td>

                                {/* ── Match points ───────────────────────── */}
                                <td className="pointsNotationValue">
                                    {matchPoints}
                                    <span style={{ fontSize: "0.75em" }}>/{maxPoints}</span>
                                </td>

                                {/* ———— Only in first row with number of players rowSpan */}
                                {index === 0 && <td rowSpan={ordered.length} style={{ color: "#ffffff" }} className="currentPointsNotation">{pendingPts}</td>}
                            </tr>
                        )
                    })}
                </tbody>
            </table>

            {/* ── Current trick points at stake ───────────────────────────────── */}
            {/* <div
                className="currentPointsNotation"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
            >
                <span style={{ fontSize: "1.4em", fontWeight: 700, lineHeight: 1 }}>
                    {pendingPts}
                </span>
                <span style={{ fontSize: "0.6em", color: "#a3a3a3", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {betLabel ?? "pts"}
                </span>
            </div> */}
        </div>
    )
}

export default memo(PointsAndScoring)